import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getDocs, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useAuth } from "../auth";
import { dishTitle, useI18n } from "../lib/i18n";
import { History } from "../components/History";
import { AccessLog } from "../components/AccessLog";
import { PrepFailureAlert } from "../components/PrepFailureAlert";
import { ReviewBar, StageBadge, StageDetail } from "../components/Progress";
import { RenameDish } from "../components/RenameDish";
import { DeleteDish, DeletedBanner } from "../components/DeleteDish";
import { ScoreWeights } from "../components/ScoreWeights";
import { MaterialLink, MaterialPreview } from "../components/MaterialPreview";
import { manualIsStale, type AuditRow, type JobRow, type UserLike } from "../lib/history";
import { materialsOf } from "../lib/materials";
import { completenessOf, minutes, TIER_HELP, TIER_LABEL, type Completeness } from "../lib/completeness";
import { progressOf, STAGE_HELP } from "../lib/progress";
import { fetchFromProxy } from "../lib/session";
import { type EditDoc } from "../lib/manual-edits";
import { type AuditLogDoc, type DishDoc, type JobDoc, type UserDoc } from "../lib/types";

const functions = getFunctions(app, "asia-northeast1");
const generateManual = httpsCallable<{ dishId: string; runs?: number }, { jobId: string }>(
  functions,
  "generateManual",
);
const applyAnswers = httpsCallable<
  { dishId: string; approve?: boolean; summary?: string },
  { jobId: string }
>(functions, "applyAnswers");
const generateTranslation = httpsCallable<{ dishId: string }, { jobId: string }>(
  functions,
  "generateTranslation",
);
/** 仕込み(動画外の前工程)の紐づけ。判断は人、反映は機械(#18) */
const linkPrep = httpsCallable<
  { dishId: string; ingredient: string; prepDishId?: string; external?: boolean; reset?: boolean },
  { jobId: string }
>(functions, "linkPrep");

/** 履歴に出す件数。品目1つの経緯を追うにはこれで足りる */
const HISTORY_LIMIT = 50;

export function DishDetail() {
  const { dishId } = useParams<{ dishId: string }>();
  const { profile, paths } = useAuth();
  const { t, lang } = useI18n();
  const [dish, setDish] = useState<DishDoc | null>(null);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [audits, setAudits] = useState<AuditRow[]>([]);
  const [users, setUsers] = useState<Record<string, UserLike>>({});
  const [completeness, setCompleteness] = useState<Completeness | null>(null);
  /** 仕込みSOPとして選べる品目（生成済みの他の品目） */
  const [prepChoices, setPrepChoices] = useState<{ id: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isStaff = profile?.role === "admin" || profile?.role === "chef";
  // 品目の削除・復元は管理者のみ(functions/delete-dish.mjs・firestore.rules と同じ線引き)
  const isAdmin = profile?.role === "admin";

  useEffect(() => {
    if (!dishId || !paths) return;
    return onSnapshot(
      paths.dishDoc(dishId),
      (s) => setDish(s.exists() ? (s.data() as DishDoc) : null),
      (e) => setError(e.message),
    );
  }, [dishId, paths]);

  useEffect(() => {
    if (!dishId || !paths) return;
    const q = query(paths.jobs(dishId), orderBy("createdAt", "desc"), limit(HISTORY_LIMIT));
    return onSnapshot(q, (snap) =>
      setJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as JobDoc) }))),
    );
  }, [dishId, paths]);

  // 人の操作（素材の差し替え等）は台帳に残る。jobs と混ぜて1本の履歴にする
  useEffect(() => {
    if (!dishId || !paths) return;
    const q = query(
      paths.auditLogs(),
      where("dishId", "==", dishId),
      orderBy("createdAt", "desc"),
      limit(HISTORY_LIMIT),
    );
    return onSnapshot(
      q,
      (snap) => setAudits(snap.docs.map((d) => ({ id: d.id, ...(d.data() as AuditLogDoc) }))),
      // 台帳はスタッフしか読めない。読めないこと自体は品目の表示を妨げない
      () => setAudits([]),
    );
  }, [dishId, paths]);

  // 表示名の引き直し。**作業者は他人の users を読めない**ので、スタッフのときだけ引く
  // （読めない場合はジョブに焼き込んだ表示名で出す）
  useEffect(() => {
    if (!paths || !isStaff) return;
    let alive = true;
    getDocs(paths.users())
      .then((snap) => {
        if (!alive) return;
        const map: Record<string, UserLike> = {};
        for (const d of snap.docs) {
          const u = d.data() as UserDoc;
          map[d.id] = { displayName: u.displayName, email: u.email };
        }
        setUsers(map);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [paths, isStaff]);

  /**
   * マニュアルの完成度。analysis.json を認可プロキシ越しに読んで組み立てる。
   *
   * Firestore に持たせないのは、**同じ数字の置き場所を2つにしないため**。
   * analysis.json が正で、作り直せば自動的に新しい値になる。
   * 取得に失敗しても画面は成立させる（完成度が出ないだけで、マニュアルは読める）。
   */
  useEffect(() => {
    if (!dishId || !dish?.currentAnalysisPath) {
      setCompleteness(null);
      return;
    }
    let alive = true;
    fetchFromProxy(`/d/${dishId}/analysis.json`)
      .then(async (r) => {
        if (!alive || !r.ok) return;
        setCompleteness(completenessOf(await r.json()));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [dishId, dish?.currentAnalysisPath, dish?.updatedAt]);

  // 仕込みSOPの選択肢。生成済みの品目だけを出す（未生成を紐づけても開けない）
  useEffect(() => {
    if (!paths || !isStaff || !completeness?.missingPrep.length) return;
    let alive = true;
    getDocs(paths.dishes())
      .then((snap) => {
        if (!alive) return;
        setPrepChoices(
          snap.docs
            // 削除済みの品目は新しく紐づけない
            .filter((d) => d.id !== dishId && !!(d.data() as DishDoc).currentAnalysisPath && !(d.data() as DishDoc).deletedAt)
            .map((d) => ({ id: d.id, name: (d.data() as DishDoc).name })),
        );
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [paths, isStaff, dishId, completeness?.missingPrep.length]);

  async function onLinkPrep(ingredient: string, choice: string) {
    setBusy(true);
    setError(null);
    try {
      await linkPrep(
        choice === "external"
          ? { dishId: dishId!, ingredient, external: true }
          : { dishId: dishId!, ingredient, prepDishId: choice },
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // 工程の削除依頼(#174)。反映ボタンの近くに件数を出す(押す前に何が起きるか分かるように)
  const [removalCount, setRemovalCount] = useState(0);
  const [editCount, setEditCount] = useState(0);
  const [manualEditCount, setManualEditCount] = useState(0);
  // 中身は仕込みの失敗アラート(作り方を入力した後の「反映待ち」)で使う
  const [manualEdits, setManualEdits] = useState<Record<string, EditDoc>>({});
  useEffect(() => {
    if (!paths || !dishId) return;
    const stopA = onSnapshot(
      paths.stepRemovals(dishId),
      (snap) => setRemovalCount(snap.size),
      () => setRemovalCount(0),
    );
    // 材料表の追加・削除依頼(#194)
    const stopB = onSnapshot(
      paths.ingredientEdits(dishId),
      (snap) => setEditCount(snap.size),
      () => setEditCount(0),
    );
    // マニュアル本体の編集(#203)
    const stopC = onSnapshot(
      paths.manualEdits(dishId),
      (snap) => {
        setManualEditCount(snap.size);
        const next: Record<string, EditDoc> = {};
        snap.docs.forEach((d) => {
          next[d.id] = d.data() as EditDoc;
        });
        setManualEdits(next);
      },
      () => {
        setManualEditCount(0);
        setManualEdits({});
      },
    );
    return () => {
      stopA();
      stopB();
      stopC();
    };
  }, [paths, dishId]);

  const active = jobs.find((j) => j.status === "queued" || j.status === "running");
  // 承認を押したのに適用されなかった実行(未解消が残っていた)。ジョブは成功で終わるため、
  // ここで理由を出さないと「押しても何も起きない」ように見える
  const approveBlocked = jobs.find(
    (j) => j.type === "approve" && j.status === "succeeded" && j.approveApplied === false,
  );

  async function onApply(approve: boolean) {
    if (!dishId || !paths) return;
    if (approve && !window.confirm(
      t("承認すると版数が上がり、以後この内容がゴールデン(正)になります。よろしいですか?"),
    )) return;
    setBusy(true);
    setError(null);
    try {
      await applyAnswers({ dishId, approve });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onGenerate() {
    if (!dishId || !paths) return;
    setBusy(true);
    setError(null);
    try {
      await generateManual({ dishId, runs: 3 });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onTranslate() {
    if (!dishId || !paths) return;
    setBusy(true);
    setError(null);
    try {
      await generateTranslation({ dishId });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (error && !dish) return <div className="page"><p className="error">{error}</p></div>;
  if (!dish) return <div className="page"><p className="muted">{t("読み込み中…")}</p></div>;

  const materials = materialsOf(dish);
  // 段階の判定は一覧と同じ純ロジックを通す（画面ごとに違う段階を出さない）
  const progress = progressOf(dish);
  // 素材を差し替えた後にまだ生成し直していない状態。黙って古い本文を出さない
  const stale = manualIsStale(dish.materialsUpdatedAt, jobs);
  // 確認事項が0問なら「レビュー・回答」は押しても「確認事項はありません」と出るだけ(#135)。
  // 進捗を計測していない古い品目(review を持たない)は判断できないので出す
  const hasQuestions = !dish.review || progress.remaining > 0;

  return (
    <div className="page">
      <p><Link to="/">{t("← 品目一覧")}</Link></p>
      <header className="page-head">
        <h1>{dishTitle(dish, lang)}</h1>
        <StageBadge p={progress} />
        <ReviewBar p={progress} />
      </header>
      {lang === "ja" && dish.nameJa && <p className="muted">{dish.nameJa}</p>}
      {dish.deletedAt && <DeletedBanner dishId={dishId!} dish={dish} isAdmin={isAdmin} />}
      {isStaff && !renaming && !deleting && (
        <p className="dish-actions">
          <button className="link" onClick={() => setRenaming(true)}>
            {t("料理名を変更する")}
          </button>
          {isAdmin && !dish.deletedAt && (
            <button className="link danger-link" disabled={!!active} onClick={() => setDeleting(true)}>
              {t("この品目を削除する")}
            </button>
          )}
        </p>
      )}
      {isStaff && renaming && (
        <RenameDish dishId={dishId!} dish={dish} onClose={() => setRenaming(false)} />
      )}
      {isAdmin && deleting && <DeleteDish dishId={dishId!} dish={dish} onClose={() => setDeleting(false)} />}
      {/* 「いまどこで、次に何をするか」を1行で出す。段階のバッジだけでは
          次の操作（回答する／反映する／承認する）が分からない */}
      {/* 仕込みの取り込みの失敗は、理由と選択肢をここ(見出しの直下)でそのまま出す。
          「確認画面で選んでください」と別の画面の奥へ行かせない(2026-09-30 柴田の指示) */}
      {isStaff && dishId && dish.failedJob?.type === "prep-doc" && dish.failedJob.ingredient ? (
        <PrepFailureAlert
          dishId={dishId}
          ingredient={dish.failedJob.ingredient}
          jobs={jobs}
          recipeDocs={materials.docs}
          edits={manualEdits}
          busy={busy || !!active}
        />
      ) : (
        <p className="stage-note">
          <StageDetail dish={dish} p={progress} />{" "}
          {/* 残りがあれば、その場から残りの質問へ行けるようにする(レビュー画面の先頭に一覧がある) */}
          {progress.remaining > 0 && progress.stage !== "generating" && (
            <>
              <Link to={`/dishes/${dishId}/review`}>{t("残りの確認事項を見る")}</Link>{" "}
            </>
          )}
          <span className="muted small">{t(STAGE_HELP[progress.stage])}</span>
        </p>
      )}

      <div className="card">
        <div className="card-head">
          <h2>{t("アップロード済みの素材")}</h2>
          <span className="spacer" />
          {isStaff && (
            <Link className="link" to={`/dishes/${dishId}/materials`}>
              {t("素材を差し替える")}
            </Link>
          )}
        </div>
        {/* 素材を開けるのは管理者と料理長のみ（未編集の生の映像。#13） */}
        <dl className="kv">
          <dt>{t("調理動画")}</dt>
          <dd>
            {materials.videos.length === 0 ? (
              "—"
            ) : (
              <ol className="files">
                {materials.videos.map((p, i) => (
                  <li key={p}>
                    {materials.videos.length > 1 && <span className="no">{t("{n}本目", { n: i + 1 })}</span>}
                    {isStaff ? (
                      <MaterialLink dishId={dishId!} path={p} onOpen={setPreview} />
                    ) : (
                      <code>{p}</code>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </dd>
          <dt>{t("レシピ・メモ")}</dt>
          <dd>
            {materials.docs.length === 0 ? (
              "—"
            ) : (
              <ul className="files">
                {materials.docs.map((p) => (
                  <li key={p}>
                    {isStaff ? (
                      <MaterialLink dishId={dishId!} path={p} onOpen={setPreview} />
                    ) : (
                      <code>{p}</code>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </dl>
        {isStaff && materials.videos.length > 0 && (
          <p className="muted small">{t("名前を押すと中身を確認できます。")}</p>
        )}
      </div>

      <div className="card">
        <h2>{t("マニュアル")}</h2>
        {stale && (
          <div className="warn">
            ⚠ {t("素材を差し替えた後、まだ生成し直していません。表示されているマニュアルは差し替え前の素材から作られたものです。")}
          </div>
        )}
        {dish.currentAnalysisPath ? (
          <>
            <Link className="button" to={`/dishes/${dishId}/view`}>{t("マニュアルを開く")}</Link>
            {hasQuestions && (
              <>
                {" "}
                <Link className="button" to={`/dishes/${dishId}/review`}>{t("レビュー・回答")}</Link>
              </>
            )}
            {isStaff && (
              <>
                {" "}
                <Link className="button" to={`/dishes/${dishId}/edit`}>{t("マニュアルを編集")}</Link>
              </>
            )}
            <p className="muted small">
              {/* 中国語版の案内は、実際に生成されている品目にだけ出す */}
              {dish.docLangs?.includes("zh")
                ? t("画面右上の言語切り替え（日本語｜中文）で、マニュアルも日本語版と中国語版が切り替わります。")
                : null}
              {hasQuestions
                ? t("確認事項への回答は「レビュー・回答」から、優先度で絞り込んで進められます。")
                : t("残っている確認事項はありません。")}
              {isStaff && t("作っていくうちに決まった変更（材料の差し替え・手順の直し）は「マニュアルを編集」から直せます。")}
            </p>
            {completeness && (
              /* 既定は閉じる。普段は段階だけ見えれば足り、開くと内訳が出る */
              <details className="completeness">
                <summary>
                  <span className="ttl">{t("この品目の完成度")}</span>
                  <span className={`badge tier-${completeness.tier}`}>
                    {t(TIER_LABEL[completeness.tier])}
                  </span>
                </summary>
                <p className="muted small">{t(TIER_HELP[completeness.tier])}</p>
                <dl className="kv metrics">
                  <dt>{t("工程")}</dt>
                  <dd>{t("{n} 工程", { n: completeness.steps })}</dd>
                  <dt>{t("動画の尺")}</dt>
                  <dd>
                    {t("{n} 分", { n: minutes(completeness.durationSec) })}
                    {completeness.activeSec > 0 &&
                      t("（うち実作業 {n} 分）", { n: minutes(completeness.activeSec) })}
                  </dd>
                  <dt>{t("撮影率")}</dt>
                  <dd>
                    {completeness.coveredRatio === null
                      ? "—"
                      : t("{n}%", { n: Math.round(completeness.coveredRatio * 100) })}
                    {completeness.uncoveredSec > 0 &&
                      t("（未記録 {n} 分）", { n: minutes(completeness.uncoveredSec) })}
                  </dd>
                  <dt>{t("食材")}</dt>
                  <dd>{t("{n} 種", { n: completeness.ingredients })}</dd>
                  <dt>{t("仕込みSOP未作成")}</dt>
                  <dd>
                    {completeness.missingPrep.length === 0 ? (
                      t("なし")
                    ) : isStaff ? (
                      /* 判断は人がする。機械は「未作成」としか言えない(#18) */
                      <ul className="prep">
                        {completeness.missingPrep.map((name) => (
                          <li key={name}>
                            <span className="nm">{name}</span>
                            <select
                              defaultValue=""
                              disabled={busy || !!active}
                              onChange={(e) => {
                                const v = e.target.value;
                                e.target.value = "";
                                if (v) void onLinkPrep(name, v);
                              }}
                            >
                              <option value="">{t("紐づける…")}</option>
                              <option value="external">{t("市販品・外部調達（仕込みSOP不要）")}</option>
                              {prepChoices.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <>
                        {t("{n} 件", { n: completeness.missingPrep.length })}
                        <span className="muted">{"：" + completeness.missingPrep.join("、")}</span>
                      </>
                    )}
                  </dd>
                  <dt>{t("要確認")}</dt>
                  <dd>{t("{n} 件", { n: completeness.disputed })}</dd>
                  <dt>{t("警告")}</dt>
                  <dd>{t("{n} 件", { n: completeness.warnings })}</dd>
                </dl>
                <p className="muted small">
                  {t("撮影率は、動画のうち工程または下準備として記録された割合です。低い品目はSOPに「撮影範囲外」が並ぶため、レシピ・メモでの補足が要ります。")}
                  {t("「仕込みSOP未作成」は、調理開始時点で既に出来ていた食材のうち作り方が記録されていないものです。市販品なら対象から外せます。")}
                </p>
              </details>
            )}
          </>
        ) : (
          <p className="muted">{t("まだ生成されていません。")}</p>
        )}

        {isStaff && (
          <>
            <button onClick={onGenerate} disabled={busy || !!active || materials.videos.length === 0}>
              {active ? t("生成中…") : busy ? t("起動中…") : dish.currentAnalysisPath ? t("作り直す") : t("マニュアルを生成")}
            </button>
            <p className="muted small">
              {t("3回生成して合議し、食い違いを検出します（数分〜十数分かかります）。")}
              {dish.currentAnalysisPath &&
                t(" 作り直すと、これまでの回答と反映済みの内容はレシピ・メモと同じ前提として解析に渡り、区間が同じ工程・同じ食材にはそのまま引き継がれます（中国語版の翻訳も引き継がれます）。")}
            </p>
            {dish.currentAnalysisPath && (
              <>
                <button onClick={onTranslate} disabled={busy || !!active}>
                  {active
                    ? t("処理中…")
                    : dish.docLangs?.includes("zh")
                      ? t("中国語版を更新")
                      : t("中国語版を生成")}
                </button>
                <p className="muted small">
                  {t("本文を繁体字中国語に翻訳し、中国語版マニュアルを組版します。翻訳済みの文は再利用し、変わった箇所だけ翻訳します。")}
                  {dish.docLangs?.includes("zh") &&
                    t(" 回答の反映などで本文が変わった箇所は日本語のまま表示されるため、そのときはここから更新してください。")}
                </p>
              </>
            )}
          </>
        )}
        {error && <p className="error">{error}</p>}
      </div>

      {isStaff && dish.currentAnalysisPath && (
        <div className="card">
          <h2>{t("回答の反映と承認")}</h2>
          <button onClick={() => onApply(false)} disabled={busy || !!active}>
            {active ? t("処理中…") : t("回答を本文に反映")}
          </button>
          {" "}
          <button onClick={() => onApply(true)} disabled={busy || !!active || dish.isGolden}>
            {dish.isGolden
              ? dish.version > 0
                ? t("承認済み(第{n}版)", { n: dish.version })
                : t("承認済み")
              : t("承認する")}
          </button>
          <p className="muted small">
            {t("反映は「レビュー・回答」で保存した回答を本文へ落とし、SOPを組版し直します。")}
            {t("承認は未解消の項目が無いときだけ版数が上がります。判断は料理長、反映は運用者、機械は判断しません。")}
          </p>
          {removalCount > 0 && (
            <div className="warn">
              {t("削除する工程が {n} 件あります。反映すると本文から外れ、工程番号は振り直されます。", { n: removalCount })}
            </div>
          )}
          {approveBlocked && !dish.isGolden && (
            <div className="warn coverage-warn">
              <strong>{t("承認は適用されませんでした")}</strong>{" "}
              {t("未解消の確認事項が {n} 件あります。「レビュー・回答」で答えてから、もう一度「承認する」を押してください。空欄のまま保存した保留も未解消として残ります。", { n: approveBlocked.remaining ?? 0 })}
            </div>
          )}
          {editCount > 0 && (
            <div className="warn">
              {t("材料表の追加・削除が {n} 件あります。反映すると材料表と工程の参照が変わります。", { n: editCount })}
            </div>
          )}
          {manualEditCount > 0 && (
            <div className="warn">
              {t("マニュアルの編集が {n} 件あります。反映すると本文が書き換わります。", { n: manualEditCount })}
            </div>
          )}
        </div>
      )}

      {/* 採点の重みは作業評価で使う。変えられるのは管理者と料理長、作業者には値だけ見せる */}
      <ScoreWeights dishId={dishId!} dish={dish} canEdit={isStaff} />

      <History
        dishId={dishId!}
        jobs={jobs}
        audits={audits}
        users={users}
        onOpenMaterial={setPreview}
      />

      {/* 閲覧記録(#13)。読めるのは管理者と料理長だけ(firestore.rules の access_logs) */}
      {isStaff && dishId && <AccessLog dishId={dishId} users={users} />}

      {preview && dishId && (
        <MaterialPreview dishId={dishId} path={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
